import React from "react";

import POVBlock from "../models/POVBlock/POVBlock";
import { ContentGrid } from '../models/Sections';

import SEO from "../components/seo";
import JsonLd from "../components/jsonld";
import Layout from "../components/layout";
import AwardCarousel from "../components/award-carousel";
import ImageWithTitlesetCTACards from "../components/about-us/image-with-titleset-cta-cards";
import ImageWithTitleset from "../components/about-us/image-with-titleset";
import ElevatedContentCard from "../components/about-us/elevated-content";
import { VideoCarousel } from "../components/video-carousel";
import renderOfficeCarousel from "../components/about-us/office-carousel";
import renderAboutUsOurImpact from '../components/about-us/our-impact';
import { renderFooterCTA } from "../components/careers/footer-cta";

import {
  AnimateIn,
  colors,
  Container,
  Heading,
  InternalLink,
  TitleSet,
  media,
  Paper,
  Text
} from '../ui-components';

import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import { EN_AU, EN_UK } from "../utils/geo-space-id-helpers";

/**
 * Places raw student data grabbed from queries into designated model to be used more efficiently
 *
 * @function careersDataIntoModel
 * 
 * @prop {object} CONTENT
 * 
 * @prop {object} rawData
 * 
 * @prop {string} geoSpace
 * 
 * @returns {JSX}
 */
const careersDataIntoModel = (CONTENT, rawData, geoSpace) => {
  const heroImage = rawData?.careersHeroImage?.childImageSharp?.gatsbyImageData

  const videoSectionContent = {
    pointOfViewTitle: CONTENT.VIDEO_SECTION__TITLE,
    pointOfViewBody: CONTENT.VIDEO_SECTION__BODY,
    pointOfViewVideo: { embedUrl: CONTENT.VIDEO_SECTION__URL },
    imageLeft: true,
    videoLandscape: true
  }
  const videoSection = new POVBlock(videoSectionContent);

  const lifeAtCrederaImage = rawData?.lifeAtCrederaImage?.childImageSharp?.gatsbyImageData;
  const lifeAtCrederaContent = {
    pointOfViewImage: { gatsbyImageData : lifeAtCrederaImage },
    pointOfViewLabel: CONTENT.LIFE_AT_CREDERA__LABEL,
    pointOfViewTitle: CONTENT.LIFE_AT_CREDERA__TITLE,
    pointOfViewBody: CONTENT.LIFE_AT_CREDERA__BODY,
    ctaText: CONTENT.LIFE_AT_CREDERA__LINK,
    ctaUrl: CONTENT.LIFE_AT_CREDERA__URL,
  }
  const lifeAtCredera = new POVBlock(lifeAtCrederaContent);

  const elevatedContentImage = rawData?.elevatedContentImage?.childImageSharp?.gatsbyImageData;
  const elevatedTitle = (geoSpace === EN_UK || geoSpace === EN_AU)
    ? CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__TITLE_UK_AND_AU
    : CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__TITLE;
  const elevatedBody = (geoSpace === EN_UK || geoSpace === EN_AU)
    ? CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__BODY_UK_AND_AU
    : CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__BODY;
  const elevatedUrl = (geoSpace === EN_UK || geoSpace === EN_AU)
    ? CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__URL_UK_AND_AU
    : CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__URL;
  const elevatedContentContent = {
    pointOfViewImage: { gatsbyImageData : elevatedContentImage },
    pointOfViewTitle: elevatedTitle,
    pointOfViewBody: elevatedBody,
    ctaText: CONTENT.LIFE_AT_CREDERA__ELEVATED_CONTENT__LINK,
    ctaUrl: elevatedUrl
  }
  const elevatedContent = new POVBlock(elevatedContentContent);

  const videoCarousel = rawData?.carouselVideos;

  const ourCultureImage = rawData?.ourCulture?.childImageSharp?.gatsbyImageData
  const ourCultureContent = {
    pointOfViewImage: { gatsbyImageData : ourCultureImage},
    pointOfViewLabel: CONTENT.OUR_CULTURE__LABEL,
    pointOfViewTitle: CONTENT.OUR_CULTURE__TITLE,
    pointOfViewBody: CONTENT.OUR_CULTURE__BODY,
  }
  const ourCultureCards = CONTENT.OUR_CULTURE__CARDS;
  const ourCulture = new POVBlock(ourCultureContent);

  const careersAtCrederaContent = {
    pointOfViewLabel: CONTENT.CAREERS_AT_CREDERA__LABEL,
    pointOfViewTitle: CONTENT.CAREERS_AT_CREDERA__TITLE,
    pointOfViewBody: CONTENT.CAREERS_AT_CREDERA__BODY,
  }
  const careersAtCredera = new POVBlock(careersAtCrederaContent);
  const careersAtCrederaStudentImage = rawData?.careersAtCrederaStudentsImage?.childImageSharp?.gatsbyImageData;
  const careersAtCrederaExperiencedProfessionalsImage = rawData?.careersAtCrederaExperiencedProfessionalsImage?.childImageSharp?.gatsbyImageData;
  const careersAtCrederaCards = [
    {
      pointOfViewImage: { gatsbyImageData : careersAtCrederaStudentImage },
      pointOfViewTitle: CONTENT.CAREERS_AT_CREDERA__STUDENTS__TITLE,
      bodyString: CONTENT.CAREERS_AT_CREDERA__STUDENTS__BODY,
      ctaUrl: CONTENT.CAREERS_AT_CREDERA__STUDENTS__URL,
      ctaText: CONTENT.CAREERS_AT_CREDERA__CTA,
    },
    {
      pointOfViewImage: { gatsbyImageData : careersAtCrederaExperiencedProfessionalsImage },
      pointOfViewTitle: CONTENT.CAREERS_AT_CREDERA__EXPERIENCED_PROFESSIONALS__TITLE,
      bodyString: CONTENT.CAREERS_AT_CREDERA__EXPERIENCED_PROFESSIONALS__BODY,
      ctaUrl: CONTENT.CAREERS_AT_CREDERA__EXPERIENCED_PROFESSIONALS__URL,
      ctaText: CONTENT.CAREERS_AT_CREDERA__CTA,
    }
  ]
  const awards = new ContentGrid(rawData?.awards);

  const offices = rawData?.globalOffices?.carouselItems;

  return { 
    heroImage,
    videoSection,
    lifeAtCredera,
    elevatedContent,
    videoCarousel,
    ourCulture,
    ourCultureCards,
    careersAtCredera,
    careersAtCrederaCards,
    awards,
    offices,
  };
};

/**
 * Renders the SEO metatag and structured data elements
 *
 * @function renderSEO
 * 
 * @prop {object} data
 * 
 * @prop {object} CONTENT
 * 
 * @prop {string} geoSpace
 * 
 * @returns {JSX}
 */
const renderSEO = (data, CONTENT, geoSpace) => {
  const image = data?.careersHeroImage?.childImageSharp;
  const slug = geoSpace ? `${geoSpace}/careers'}` : '/careers';

  return (
    <>
      <SEO
        title={CONTENT.SEO_TITLE}
        description={CONTENT.SEO_DESCRIPTION}
        slug={slug}
        image={image?.gatsbyImageData?.images?.fallback?.src}
      />
      <JsonLd
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: CONTENT.SEO_TITLE,
          alternateName: CONTENT.TITLE,
          description: CONTENT.SEO_DESCRIPTION,
          abstract: CONTENT.SEO_DESCRIPTION,
          headline: CONTENT.SUBTITLE,
          alternativeHeadline: CONTENT.VIDEO__TITLE,
          inLanguage: CONTENT.IN_LANGUAGE,
          url: `http://credera.com/${slug}`,
          isAccessibleForFree: true,
          image: image?.gatsbyImageData?.images?.fallback?.src
          // significantLink: TODO - Add link to job openings
        }}
      />
    </>
  );
};

/**
 * Renders the hero section
 *
 * @function renderTitleSection
 * @returns {JSX}
 */
const renderHeroSection = (CONTENT, heroSectionImage, geoSpace) => {

  return (
    <OverviewHeroSection>
      {heroSectionImage && <HeroImageTop image={heroSectionImage} alt={`${CONTENT.TITLE} Hero Image`}/>}
      <OverviewHeroTitleSection>
        <Header>
          <Heading level={1}>
            {CONTENT.TITLE}
          </Heading>
          <StyledText>{CONTENT.SUBTITLE}</StyledText>
        </Header>
        {geoSpace === EN_AU ? (
          <PrimaryButton linkstyle="primary-button" to={CONTENT.JOBS__BUTTON__URL}>
            {CONTENT.JOBS__BUTTON}
          </PrimaryButton>
        ) : (
        <ButtonContainer>
          <PrimaryButton linkstyle="primary-button" to={CONTENT.PRIMARY_BUTTON_URL}>
            {CONTENT.PRIMARY_BUTTON}
          </PrimaryButton>
          <SecondaryButton linkstyle="secondary-button" to={CONTENT.SECONDARY_BUTTON_URL}>
            {CONTENT.SECONDARY_BUTTON}
          </SecondaryButton>
        </ButtonContainer>
        )}
      </OverviewHeroTitleSection>
    </OverviewHeroSection>
  );
};

const renderVideoSection = (videoSection, geoSpace) => {
  return (
    <ImageWithTitleset 
      contentBlock={videoSection} 
      imageLeft={videoSection?.imageLeft} 
      geoSpace={geoSpace} 
      centerTextSection 
      landscape={videoSection.videoLandscape}/>
  );
};

const renderlifeAtCrederaSection = (lifeAtCredera, elevatedContent, geoSpace) => {
  return (
    <>
      <ImageWithTitleset
        geoSpace={geoSpace}
        contentBlock={lifeAtCredera}
        centerTextSection
        altMobileOrder
      />
      <ElevatedContentCard content={elevatedContent} />
    </>
  );
};

const renderVideoCarouselSection = (CONTENT, videoCarousel) => { 
  return (
    <AnimateIn onScroll>
      <VideoCarousel 
        subtitle={CONTENT.VIDEO_CAROUSEL__TITLE}
        carouselVideos={videoCarousel.videos} />
    </AnimateIn>
  );
};

const renderOurCultureSection = (ourCulture, contentCards, geoSpace, secondaryColor) => {
  if (contentCards.length) {
    const ourCultureCards = contentCards.map(card => {
      return (
        <ImageWithTitlesetCTACards
          title={card.TITLE}
          noContent
          layout={'wide'}
          slug={card.LINK}
          secondaryColor={secondaryColor}
          ctaText={card.OUR_CULTURE__LINK}
          geoSpace={geoSpace}
        />
      );
    });

    return (
      <>
        <ImageWithTitleset 
          contentBlock={ourCulture} 
          altMobileOrder 
          />
        <CTAGridCards>
          <AnimateIn onScroll>
            <CardGrid>{ourCultureCards}</CardGrid>
          </AnimateIn>
        </CTAGridCards>
      </>
    );
  }
};

const renderAwardsSection = (CONTENT, awards) => {
  return (
    <Container sectionContainer awardContainer>
      <AnimateIn onScroll>
        <TitleWrapper>
          <TitleSet
            label={CONTENT.AWARDS__LABEL}
            title={CONTENT.AWARDS__TITLE}
            bodyText={CONTENT.AWARDS__BODY}
          />
        </TitleWrapper>
      </AnimateIn>
      {awards && <AwardCarousel awards={awards}></AwardCarousel>}
    </Container>
  );
};

const renderCareersOverview = (rawData, CONTENT, geoSpace) => {

  const sectionData = careersDataIntoModel(CONTENT, rawData, geoSpace);
  
  const { 
    heroImage, 
    videoSection,
    lifeAtCredera,
    elevatedContent,
    videoCarousel,
    ourCulture,
    ourCultureCards,
    careersAtCredera,
    careersAtCrederaCards,
    awards,
    offices,
  } = sectionData;

  const includeCareersSection = (
    <>
      <Paper top overlay>
        {videoCarousel && renderVideoCarouselSection(CONTENT, videoCarousel)}
        {ourCulture && ourCultureCards && renderOurCultureSection(ourCulture, ourCultureCards, geoSpace)}
      </Paper>
      <Paper bottom subtle />
      {careersAtCredera && careersAtCrederaCards && renderAboutUsOurImpact(careersAtCredera, careersAtCrederaCards)} 
      <Paper top>
        {awards && renderAwardsSection(CONTENT, awards)}
        {offices && offices.length > 0 && renderOfficeCarousel(offices, CONTENT.OFFICES__TITLE, CONTENT.OFFICES__CTA)}
        {renderFooterCTA(CONTENT, geoSpace)}
      </Paper>
    </>
  )
  
  const excludeCareersSection =  (
    <>
      <Paper top overlay>
        {videoCarousel && renderVideoCarouselSection(CONTENT, videoCarousel)}
        {ourCulture && ourCultureCards && renderOurCultureSection(ourCulture, ourCultureCards, geoSpace)}
        {awards && renderAwardsSection(CONTENT, awards)}
        {offices && offices.length > 0 && renderOfficeCarousel(offices, CONTENT.OFFICES__TITLE, CONTENT.OFFICES__CTA)}
        {renderFooterCTA(CONTENT, geoSpace)}
      </Paper>
    </>
  )

  return (
    <Layout>
      <Paper bottom>
        {renderSEO(sectionData, CONTENT, geoSpace)}
        {renderHeroSection(CONTENT, heroImage, geoSpace)}
        {videoSection && renderVideoSection(videoSection, geoSpace)}
      </Paper>
        {lifeAtCredera && elevatedContent && renderlifeAtCrederaSection(lifeAtCredera, elevatedContent, geoSpace)}
        {geoSpace === EN_AU ? excludeCareersSection : includeCareersSection}
    </Layout>
  );
};


const OverviewHeroSection = styled(Container)`
  margin: 0 auto; 
  padding: 0px;
  margin-bottom: 20px; //to make up for the -100 top positioning so we can have 120px difference

  @media screen and (max-width: ${media.mediumTop}) {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    margin-bottom: 60px;
  }
`;

const HeroImageTop = styled(GatsbyImage)`
  display: block;
  max-width: 1440px;
  height: 450px; 

  @media screen and (max-width: ${media.mediumTop}) {
    height: 200px
  }
`;

const OverviewHeroTitleSection = styled.div`
  width: 840px;
  height: fit-content;
  position: relative;
  padding: 60px 80px;
  gap: 20px;
  background-color: ${colors.pureWhite};
  top: -180px;

  @media screen and (max-width: ${media.mediumTop}) {
    padding: 40px 64px;
    width: 75%;
    top: -60px;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    width: 100%;
    flex-direction: column;
    position: initial;
    padding: 28px 20px;
    margin-top: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ButtonContainer = styled.div`
  display: flex;
  max-width: 376px;
  gap: 16px;

  @media screen and (max-width: ${media.mediumBottom}) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PrimaryButton = styled(InternalLink)`
  padding-bottom: 14px;
  width: fit-content;

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
    flex-direction: column;
    width: 249px;
    gap: 16px;
  }
`;

const SecondaryButton = styled(InternalLink)`
  padding-bottom: 14px;
  width: fit-content;

  @media screen and (max-width: ${media.mediumBottom}) {
    text-align: center;
    flex-direction: column;
    width: 111px;
    gap: 16px;
  }
`;

const CTAGridCards = styled(Container)`
  margin-top: -240px;
  padding-bottom: 120px;

  @media screen and (max-width: ${media.largeTop}) {
    width: 100vw;
    margin-top: -120px;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    padding-top: 0px;
    margin-top: 0px;
    padding-bottom: 60px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1064px;
  margin-left: ${({ contentRight }) => (contentRight ? '275px' : '')};

  @media screen and (max-width: ${media.largeTop}) {
    display: flex;
    flex-direction: column;
    margin-left: 0px;
  }
`;

const TitleWrapper = styled.div`
  text-align: center;
`;

const StyledText = styled(props => <Text {...props} />)`
  margin-bottom: 20px;
`;

export { renderCareersOverview };
